import React from 'react';
import { Download, Files, Info, Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatAmountInput, formatRial, parseAmountDigits } from '@/lib/formatMoney';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbBatchItem,
  type BsiIbBatchLimits,
  type BsiIbTransferPreview,
} from '@/lib/banks/bsi/api';
import { downloadBsiLocalBatchSample, downloadBsiReceipt } from '@/lib/banks/bsi/download';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';
import BsiSystemShebaPicker from './BsiSystemShebaPicker';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

type ChallengeType = 'sms' | 'signature_password' | string;
type UiStep = 'form' | 'confirm' | 'done';

const BsiBatchLocalTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [step, setStep] = React.useState<UiStep>('form');
  const [draftKey, setDraftKey] = React.useState<string | null>(null);
  const [sources, setSources] = React.useState<BsiIbAccountOption[]>([]);
  const [reasonCodes, setReasonCodes] = React.useState<BsiIbAccountOption[]>([]);
  const [reasonCodesFile, setReasonCodesFile] = React.useState<BsiIbAccountOption[]>([]);
  const [items, setItems] = React.useState<BsiIbBatchItem[]>([]);
  const [limits, setLimits] = React.useState<BsiIbBatchLimits | null>(null);

  const [sourceAccount, setSourceAccount] = React.useState('');
  const [destinationAccount, setDestinationAccount] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [paymentId, setPaymentId] = React.useState('');
  const [reasonCode, setReasonCode] = React.useState('');
  const [reasonDesc, setReasonDesc] = React.useState('');
  const [fileReasonCode, setFileReasonCode] = React.useState('');
  const [fileReasonDesc, setFileReasonDesc] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);

  const [preview, setPreview] = React.useState<BsiIbTransferPreview | null>(null);
  const [confirmDraftKey, setConfirmDraftKey] = React.useState<string | null>(null);
  const [challengeType, setChallengeType] = React.useState<ChallengeType>('sms');
  const [challengeLabel, setChallengeLabel] = React.useState('شناسه تأیید پیامک');
  const [authStep, setAuthStep] = React.useState(1);
  const [challenge, setChallenge] = React.useState('');
  const [ackIrreversible, setAckIrreversible] = React.useState(false);
  const [reference, setReference] = React.useState<string | null>(null);
  const [receiptKey, setReceiptKey] = React.useState<string | null>(null);

  const loadForm = React.useCallback(async () => {
    setLoadingForm(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localBatchForm();
      setDraftKey(res.data.draft_key);
      setSources(res.data.source_accounts || []);
      setReasonCodes(res.data.reason_codes || []);
      setReasonCodesFile(res.data.reason_codes_file || res.data.reason_codes || []);
      setLimits(res.data.limits || null);
      setSourceAccount(res.data.source_accounts?.[0]?.value || '');
      setReasonCode(res.data.reason_codes?.[0]?.value || '');
      setFileReasonCode(res.data.reason_codes_file?.[0]?.value || res.data.reason_codes?.[0]?.value || '');
      setItems([]);
      setStep('form');
      setPreview(null);
      setConfirmDraftKey(null);
      setReference(null);
      setReceiptKey(null);
      onMessage({ type: 'success', text: res.message || 'فرم صادرات دسته‌ای آماده است.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'خطا در باز کردن صادرات دسته‌ای';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoadingForm(false);
    }
  }, [onMessage, onSessionExpired]);

  React.useEffect(() => {
    if (sessionValid && !draftKey && !loadingForm) void loadForm();
  }, [sessionValid, draftKey, loadingForm, loadForm]);

  const handleAddRow = async () => {
    if (!draftKey) return;
    const amountDigits = parseAmountDigits(amount);
    if (!sourceAccount || !destinationAccount.trim() || !amountDigits || !reasonCode) {
      onMessage({ type: 'error', text: 'مبدأ، شماره حساب مقصد، مبلغ و علت انتقال را کامل کنید.' });
      return;
    }
    setBusy(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localBatchAddRow({
        draft_key: draftKey,
        source_account: sourceAccount,
        destination_account: destinationAccount.replace(/\D+/g, ''),
        amount: amountDigits,
        reason_code: reasonCode,
        reason_desc: reasonDesc.trim() || undefined,
        description: description.trim() || undefined,
        payment_id: paymentId.trim() || undefined,
      });
      setItems(res.data.items || []);
      setDestinationAccount('');
      setAmount('');
      setDescription('');
      setPaymentId('');
      onMessage({ type: 'success', text: res.message || 'ردیف اضافه شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'افزودن ردیف ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async () => {
    if (!draftKey || !file) {
      onMessage({ type: 'error', text: 'فایل را انتخاب کنید.' });
      return;
    }
    if (!fileReasonCode) {
      onMessage({ type: 'error', text: 'علت انتقال فایل را انتخاب کنید.' });
      return;
    }
    setBusy(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localBatchUpload({
        draft_key: draftKey,
        reason_code: fileReasonCode,
        reason_desc: fileReasonDesc.trim() || undefined,
        file,
      });
      setItems(res.data.items || []);
      onMessage({ type: 'success', text: res.message || 'فایل بارگذاری شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'آپلود ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!draftKey) return;
    if (!items.length) {
      onMessage({ type: 'error', text: 'حداقل یک ردیف لازم است.' });
      return;
    }
    setBusy(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localBatchSend({ draft_key: draftKey });
      setPreview(res.data.preview);
      setConfirmDraftKey(res.data.confirm_draft_key);
      setChallengeType(res.data.challenge_type || 'sms');
      setChallengeLabel(res.data.challenge_label || 'شناسه تأیید پیامک');
      setAuthStep(res.data.step || 1);
      setChallenge('');
      setAckIrreversible(false);
      setStep('confirm');
      onMessage({ type: 'success', text: res.message || 'دسته آماده تأیید است.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'ارسال دسته ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmDraftKey) return;
    if (!ackIrreversible) {
      onMessage({ type: 'error', text: 'برگشت‌ناپذیر بودن را تأیید کنید.' });
      return;
    }
    if (!challenge.trim()) {
      onMessage({ type: 'error', text: 'کد تأیید را وارد کنید.' });
      return;
    }
    setBusy(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.localTransferConfirm({
        confirm_draft_key: confirmDraftKey,
        challenge: challenge.trim(),
      });
      if (res.data.needs_next_step) {
        setConfirmDraftKey(res.data.confirm_draft_key);
        setChallengeType(res.data.challenge_type || 'sms');
        setChallengeLabel(res.data.challenge_label || 'کد تأیید');
        setAuthStep(res.data.step || authStep + 1);
        setChallenge('');
        onMessage({ type: 'success', text: res.message || 'مرحله بعد را تکمیل کنید.' });
        return;
      }
      setReference(res.data.reference);
      setReceiptKey(res.data.receipt_key);
      setStep('done');
      onMessage({ type: 'success', text: res.message || 'صادرات دسته‌ای ثبت شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'تأیید ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setBusy(false);
    }
  };

  const destOf = (row: BsiIbBatchItem) => row.destination_account || row.destination_iban;

  return (
    <Card className="lg:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Files className="h-4 w-4" />
          صادرات دسته‌ای
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!sessionValid ? (
          <p className="text-sm text-amber-700">برای صادرات دسته‌ای ابتدا وارد اینترنت‌بانک شوید.</p>
        ) : (
          <>
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              <div className="mb-1 flex items-center gap-1 font-medium">
                <ShieldAlert className="h-3.5 w-3.5" />
                هشدار
              </div>
              مقصد فقط شماره حساب بانک صادرات است (نه شبا). می‌توانید ردیف دستی بزنید یا فایل نمونه بانک را آپلود کنید.
            </div>

            {limits && (
              <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <div className="mb-1.5 flex items-center gap-1 font-medium">
                  <Info className="h-3.5 w-3.5" />
                  سقف‌های اعلام‌شده بانک
                </div>
                <ul className="list-disc space-y-0.5 pr-4">
                  <li>حداکثر {limits.max_rows_per_batch} تراکنش در هر دسته</li>
                  <li>سقف هر تراکنش: {formatRial(limits.max_amount_per_tx)} ریال</li>
                  <li>سقف روزانه از هر حساب: {formatRial(limits.max_amount_per_day)} ریال</li>
                  {limits.daily_count_limit != null ? (
                    <li>تعداد انتقال روزانه: {limits.daily_count_limit}</li>
                  ) : (
                    <li>تعداد انتقال روزانه: وابسته به دسترسی حساب</li>
                  )}
                  <li>کارمزد هر تراکنش: {formatRial(limits.fee_rial)} ریال</li>
                </ul>
              </div>
            )}

            {step === 'form' && (
              <>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => void loadForm()} disabled={loadingForm}>
                    {loadingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    <span className="mr-1">بروزرسانی فرم</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      void downloadBsiLocalBatchSample('xlsx').catch((e) =>
                        onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                      )
                    }
                  >
                    <Download className="ml-1 h-4 w-4" />
                    نمونه Excel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      void downloadBsiLocalBatchSample('csv').catch((e) =>
                        onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                      )
                    }
                  >
                    <Download className="ml-1 h-4 w-4" />
                    نمونه CSV
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>حساب مبدأ</Label>
                    <SearchableSelect
                      value={sourceAccount}
                      onChange={setSourceAccount}
                      options={sources.map((a) => ({ value: a.value, label: a.label || a.value }))}
                      placeholder="مبدأ"
                    />
                  </div>
                  <BsiSystemShebaPicker
                    mode="account"
                    disabled={busy}
                    onSelect={(selection) => {
                      setDestinationAccount(selection.accountNumber);
                      if (selection.employeeLabel) {
                        if (!description.trim()) setDescription(selection.employeeLabel);
                        if (!reasonDesc.trim()) setReasonDesc(selection.employeeLabel.slice(0, 25));
                      }
                    }}
                  />
                  <div className="space-y-2">
                    <Label>شماره حساب مقصد (صادرات)</Label>
                    <Input
                      value={destinationAccount}
                      onChange={(e) => setDestinationAccount(e.target.value.replace(/[^\d]/g, ''))}
                      dir="ltr"
                      className="font-mono"
                      placeholder="شماره حساب"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>مبلغ (ریال)</Label>
                    <Input
                      value={amount}
                      onChange={(e) => setAmount(formatAmountInput(e.target.value))}
                      dir="ltr"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>شناسه پرداخت (اختیاری)</Label>
                    <Input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label>علت انتقال</Label>
                    <SearchableSelect
                      value={reasonCode}
                      onChange={setReasonCode}
                      options={reasonCodes.map((r) => ({ value: r.value, label: r.label || r.value }))}
                      placeholder="علت"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>شرح علت</Label>
                    <Input
                      value={reasonDesc}
                      onChange={(e) => setReasonDesc(e.target.value.slice(0, 25))}
                      maxLength={25}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>شرح</Label>
                    <Input value={description} onChange={(e) => setDescription(e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="button" onClick={() => void handleAddRow()} disabled={busy || loadingForm}>
                      {busy ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                      افزودن ردیف
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 rounded-md border px-3 py-3">
                  <p className="text-sm font-medium">یا آپلود فایل</p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>علت انتقال فایل</Label>
                      <SearchableSelect
                        value={fileReasonCode}
                        onChange={setFileReasonCode}
                        options={reasonCodesFile.map((r) => ({ value: r.value, label: r.label || r.value }))}
                        placeholder="علت"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>شرح علت فایل</Label>
                      <Input
                        value={fileReasonDesc}
                        onChange={(e) => setFileReasonDesc(e.target.value.slice(0, 25))}
                        maxLength={25}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>فایل Excel / CSV</Label>
                      <Input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                      />
                    </div>
                  </div>
                  <Button type="button" variant="outline" onClick={() => void handleUpload()} disabled={busy}>
                    آپلود فایل
                  </Button>
                </div>

                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="border px-2 py-1.5 text-start">مبدأ</th>
                        <th className="border px-2 py-1.5 text-start">حساب مقصد</th>
                        <th className="border px-2 py-1.5 text-start">مبلغ</th>
                        <th className="border px-2 py-1.5 text-start">شرح</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="border px-2 py-3 text-slate-500">
                            هنوز ردیفی نیست.
                          </td>
                        </tr>
                      ) : (
                        items.map((row, idx) => (
                          <tr key={`${destOf(row)}-${idx}`}>
                            <td className="border px-2 py-1.5 font-mono text-xs">{row.source_account || '—'}</td>
                            <td className="border px-2 py-1.5 font-mono text-xs">{destOf(row)}</td>
                            <td className="border px-2 py-1.5 font-mono">{formatRial(row.amount)}</td>
                            <td className="border px-2 py-1.5">{row.description || '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <Button type="button" onClick={() => void handleSend()} disabled={busy || items.length === 0}>
                  {busy ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                  ارسال دسته برای تأیید
                </Button>
              </>
            )}

            {step === 'confirm' && preview && (
              <div className="space-y-4">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  مرحله {authStep} — {challengeType === 'signature_password' ? 'رمز امضا' : 'شناسه تأیید پیامک'}
                </div>
                <div className="space-y-1 rounded-md border bg-slate-50 px-3 py-3 text-sm">
                  <p>تعداد مقصد: {items.length}</p>
                  <p>
                    جمع مبلغ: <span className="font-mono font-semibold">{formatRial(preview.amount)}</span> ریال
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{challengeLabel}</Label>
                  <Input
                    type={challengeType === 'signature_password' ? 'password' : 'text'}
                    value={challenge}
                    onChange={(e) => setChallenge(e.target.value)}
                  />
                </div>
                <label className="flex items-start gap-2 text-sm text-rose-800">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={ackIrreversible}
                    onChange={(e) => setAckIrreversible(e.target.checked)}
                  />
                  <span>جزئیات دسته را بررسی کردم و ادامه را تأیید می‌کنم.</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => setStep('form')} disabled={busy}>
                    بازگشت
                  </Button>
                  <Button type="button" variant="destructive" onClick={() => void handleConfirm()} disabled={busy}>
                    {busy ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                    تأیید
                  </Button>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="space-y-3 text-sm">
                <p className="text-emerald-700">صادرات دسته‌ای ارسال شد.</p>
                {reference && (
                  <p>
                    پیگیری: <span className="font-mono font-semibold">{reference}</span>
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {receiptKey && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          void downloadBsiReceipt(receiptKey, 'html').catch((e) =>
                            onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                          )
                        }
                      >
                        <Download className="ml-1 h-4 w-4" />
                        دانلود HTML
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          void downloadBsiReceipt(receiptKey, 'pdf').catch((e) =>
                            onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                          )
                        }
                      >
                        <Download className="ml-1 h-4 w-4" />
                        PDF
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          void downloadBsiReceipt(receiptKey, 'excel').catch((e) =>
                            onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                          )
                        }
                      >
                        <Download className="ml-1 h-4 w-4" />
                        اکسل
                      </Button>
                    </>
                  )}
                  <Button type="button" variant="outline" onClick={() => void loadForm()}>
                    دسته جدید
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BsiBatchLocalTab;
